import { useRef, useCallback } from 'react';
import { logContentActivity } from '../../containers/actions/activity_logs/activity_logs_api';

interface ActivityLoggingConfig {
  contentId: string;
  contentType: 'image' | 'video' | 'document' | 'audio' | 'article' | 'podcast' | 'playlist' | 'season';
}

export const useActivityLogging = (config: ActivityLoggingConfig) =>
{
	const { contentId, contentType } = config;

	// Refs
	const startTime = useRef<number>(0);
	const isActive = useRef<boolean>(false);
	const stopPromise = useRef<Promise<void> | null>(null); // track stopLogging promise

	// Function to start logging
	const startLogging = useCallback(async () =>
	{
		// Wait if stopLogging is still running
		if (stopPromise.current)
		{
			await stopPromise.current;
		}

		if (contentId && contentType && !isActive.current)
		{
			startTime.current = Date.now();
			isActive.current = true;
		}
	}, [ contentId, contentType ]);

	// Function to stop logging and make API call
	const stopLogging = useCallback(() =>
	{
		if (isActive.current && contentId && contentType)
		{
			const duration = Date.now() - startTime.current;

			if (duration > 0)
			{
				stopPromise.current = logContentActivity({
					contentId,
					contentType,
					duration,
					deviceType : 'web',
					actionName : 'view'
				})
					.catch((error) =>
					{
						console.error('Failed to log content activity:', error);
					})
					.finally(() =>
					{
						isActive.current = false;
						stopPromise.current = null; // clear after finished
					});

				return stopPromise.current;
			}
		}

		return Promise.resolve();
	}, [ contentId, contentType ]);

	return {
		startLogging,
		stopLogging
	};
};

export default useActivityLogging;
